import React from 'react';
import classnames from 'classnames';

import {Icon} from '@albert-io/atomic';
import type {IconColors, IconStyle} from '@albert-io/atomic/atoms/Icon/Icon.react';

type SyncStatus = 'sync' | 'synced' | 'syncedResyncOnHover' | 'error' | 'blocked';

const BADGE_CLASS = 'absolute inline-flex rounded-full background-primary p-[1px]';
const BOTTOM_RIGHT = 'bottom-[-3px] right-[-3px]';
const TOP_LEFT = 'top-[-3px] left-[-3px]';

interface BadgeProps {
  icon: 'arrows-rotate' | 'circle-check' | 'circle-exclamation';
  iconStyle: IconStyle;
  color: IconColors;
  corner: string;
  className?: string;
}

const Badge = ({icon, iconStyle, color, corner, className}: BadgeProps) => (
  <span className={classnames(BADGE_CLASS, corner, className)}>
    <Icon icon={icon} iconStyle={iconStyle} color={color} className='fa-2xs' />
  </span>
);

/**
 * Decoration layered over the host IconButton's base glyph — a rotate badge (not synced), check badge
 * (synced), warning badge (error), or slash (can't sync) — composed in code since a single-color FA-kit
 * glyph can't do the Figma's 2-color badges. `syncedResyncOnHover` is the check badge that swaps to the
 * rotate badge while the host IconButton (which must carry the `group` class) is hovered or
 * keyboard-focused. Pointer-events off so the IconButton stays the click target.
 */
export const SyncStatusOverlay = ({status}: {status: SyncStatus}) => {
  if (status === 'blocked') {
    // Tertiary so the slash matches the muted (can't-sync) base arrows.
    return (
      <span className='pointer-events-none absolute inset-[0] flex items-center justify-center'>
        <Icon icon='slash' iconStyle='regular' color='tertiary' />
      </span>
    );
  }
  return (
    <span className='pointer-events-none absolute inset-[0] flex items-center justify-center'>
      <span className='relative h-[1em] w-[1em]'>
        {status === 'error' && (
          <Badge icon='circle-exclamation' iconStyle='solid' color='warning' corner={TOP_LEFT} />
        )}
        {status === 'sync' && (
          <Badge icon='arrows-rotate' iconStyle='regular' color='inherit' corner={BOTTOM_RIGHT} />
        )}
        {(status === 'synced' || status === 'syncedResyncOnHover') && (
          <Badge
            icon='circle-check'
            iconStyle='solid'
            color='positive'
            corner={BOTTOM_RIGHT}
            className={
              status === 'syncedResyncOnHover'
                ? 'group-hover:invisible group-focus-visible:invisible'
                : undefined
            }
          />
        )}
        {status === 'syncedResyncOnHover' && (
          <Badge
            icon='arrows-rotate'
            iconStyle='regular'
            color='inherit'
            corner={BOTTOM_RIGHT}
            className='invisible group-hover:visible group-focus-visible:visible'
          />
        )}
      </span>
    </span>
  );
};
